import type { ConstructionStatusUpdateFormData, MappedAccountData } from '@/components/account/types'
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useNotes } from '@/composables/useNotes'
import { useToast } from '@/composables/useToast'
import {
	API_BASE,
	API_ENDPOINT_ACCOUNT_UPDATE_CONSTRUCTION_STATUS,
	API_ENDPOINT_PORTAL_SEND_ACTIVATION_REMINDER
} from '@/constants/index.js'
import { mapAccount } from '@/utils/mappers/AccountMap'
import { computed, ref } from 'vue'

const accountData = ref<MappedAccountData>()
const lastAccountId = ref('')
const fullAddress = computed(() => {
	const streetAddress = accountData.value?.address || ''
	const city = accountData.value?.city || ''
	const state = accountData.value?.state || ''
	const country = 'USA'
	if (!streetAddress || !city || !state) {
		return ''
	}
	return `${streetAddress}, ${city}, ${state}, ${country}`
})
const fullName = computed(() => `${accountData.value?.firstName} ${accountData.value?.lastName}`)
const accountName = computed(() => {
	return {
		title: accountData.value?.title || null,
		first: accountData.value?.firstName || '',
		middle: accountData.value?.middleName || null,
		last: accountData.value?.lastName || '',
		suffix: accountData.value?.suffix || null
	}
})
const isDelinquent = computed(() => {
	return !!accountData.value?.isDelinquent
})
const loading = ref(true)

const { addToast } = useToast()

export function useAccount() {
	const error = useError()
	const { bugsnagReport } = useErrorReporter()

	const refetchAccount = async (hideLoading?: boolean) => {
		if (lastAccountId.value) {
			if (typeof lastAccountId.value === 'number') {
				lastAccountId.value = (lastAccountId.value as number)?.toString()
			}
			await fetchAccount(lastAccountId.value, hideLoading)
		}
	}

	const fetchAccount = async (accountId: string | number, hideLoading?: boolean) => {
		// fetchAccount is a private function, getAccount is the public function that has checks to avoid refetching
		if (!hideLoading) {
			loading.value = true
		}

		if (!accountId) {
			addToast({
				title: 'Error: Missing Account ID',
				message: 'Account ID is required to fetch account data.',
				notificationType: 'error'
			})
			return
		}

		if (typeof accountId === 'number') {
			lastAccountId.value = accountId?.toString()
		} else {
			lastAccountId.value = accountId
		}

		await $fetch(`${API_BASE}/portal/accounts/${accountId}`, {
			method: 'GET',
			credentials: 'include',
			onResponse({ response }) {
				if (response.status === 204) {
					error.value = createError({
						statusCode: 204,
						statusMessage: 'Account not found.'
					})
				}
				if (!response?.ok) {
					throw new Error(response._data?.error || response._data?.errors?.[0])
				}
				accountData.value = mapAccount(response._data)
			}
		}).catch(e => {
			bugsnagReport({
				error: e,
				showToast: true,
				toast: { title: 'Error fetching account data' }
			})
			error.value = createError({
				statusCode: 404,
				statusMessage: e instanceof Error ? e.message : 'Error fetching account data'
			})
		}).finally(() => {
			loading.value = false
		})
	}

	const getAccount = async (accountId: string) => {
		if (!accountId) {
			return
		}
		if (accountId === lastAccountId.value) {
			return
		}
		await fetchAccount(accountId)
	}

	const handleSendActivationReminder = async (accountId: string) => {
		if (!accountId) {
			return
		}
		const { addToast } = useToast()
		const { getNotes } = useNotes()
		loading.value = true
		await $fetch(`${API_BASE}${API_ENDPOINT_PORTAL_SEND_ACTIVATION_REMINDER.replace('{account-id}', accountId)}`, {
			method: 'POST',
			credentials: 'include',
			onResponse({ response }) {
				if (!response?.ok) {
					throw new Error(response._data?.error || response._data?.errors?.[0]?.title)
				}
				/* TODO i18n */
				addToast({
					title: 'Success',
					message: 'An activation reminder has been sent to the user',
					notificationType: 'success'
				})
				getNotes(accountId, true)
			}
		}).catch(e => {
			/* TODO i18n */
			bugsnagReport({
				error: e instanceof Error ? e : new Error('getInvoices - catch: ' + (e as Error).toString()),
				showToast: true,
				toast: {
					title: 'Error',
					message: e instanceof Error ? e.message : 'Error sending activation reminder',
					notificationType: 'error'
				},
				user: {
					id: accountId,
					name: fullName.value,
					emailId: String(accountData.value?.emailId) || ''
				}
			})
		}).finally(() => {
			loading.value = false
		})
	}

	const updateConstructionStatus = async (accountId: string, body: ConstructionStatusUpdateFormData) => {
		const { addToast } = useToast()
		loading.value = true
		await $fetch(`${API_BASE}${API_ENDPOINT_ACCOUNT_UPDATE_CONSTRUCTION_STATUS.replace('{account-id}', accountId)}`, {
			method: 'PUT',
			credentials: 'include',
			body: {
				...body,
				insideScheduledDate: Math.floor(new Date(`${body.insideScheduledDate}T07:00:00+00:00`).getTime() / 1000),
				surveyScheduledDate: Math.floor(new Date(`${body.surveyScheduledDate}T07:00:00+00:00`).getTime() / 1000),
				outsideScheduledDate: Math.floor(new Date(`${body.outsideScheduledDate}T07:00:00+00:00`).getTime() / 1000),
				tempLine: body.tempLine?.id
			},
			onResponse({ response }) {
				if (!response?.ok) {
					throw new Error(response._data?.error || response._data?.errors?.[0]?.title)
				}
				addToast({
					title: 'Success',
					message: 'Construction status updated successfully',
					notificationType: 'success'
				})
			}
		}).catch(e => {
			bugsnagReport({
				error: e instanceof Error ? e : new Error('getInvoices - catch: ' + (e as Error).toString()),
				showToast: true,
				toast: {
					title: 'Error',
					message: e instanceof Error ? e.message : 'Error updating construction status',
					notificationType: 'error'
				},
				user: {
					id: accountId,
					name: fullName.value,
					emailId: String(accountData.value?.emailId) || ''
				}
			})
		}).finally(async () => {
			await refetchAccount()
			loading.value = false
		})
	}

	return {
		accountData,
		fullAddress,
		fullName,
		accountName,
		isDelinquent,
		hasDiscount: ref(accountData.value?.hasDiscount || false),
		getAccount,
		refetchAccount,
		loading,
		handleSendActivationReminder,
		updateConstructionStatus
	}
}
